import * as React from "react";
import { useState, useEffect } from "react";
import qrcodegen from "./qrcodegen/qrcodegen";
import {
  DEFAULT_SIZE,
  DEFAULT_LEVEL,
  DEFAULT_BGCOLOR,
  DEFAULT_FGCOLOR,
  DEFAULT_INCLUDEMARGIN,
  ERROR_LEVEL_MAP,
} from "./qrcode.types";
import type { QRProps } from "./qrcode.types";
import {
  excavateModules,
  generatePath,
  getImageSettings,
  getMarginSize,
} from "./qrcode.helpers";

function QRCode(props: QRProps) {
  const [available, setAvailable] = useState(() => false);

  const [cells, setCells] = useState(() => null);

  const [calculatedImageSettings, setCalculatedImageSettings] = useState(
    () => null
  );

  const [margin, setMargin] = useState(() => null);

  const [fgPath, setFgPath] = useState(() => null);

  function genCells() {
    return qrcodegen.QrCode.encodeText(
      props.value,
      ERROR_LEVEL_MAP[props.level]
    ).getModules();
  }

  function numCells() {
    return (cells?.length ?? 0) + (margin ?? 0) * 2;
  }

  useEffect(() => {
    setCells(genCells());
  }, []);

  useEffect(() => {
    if (props.imageSettings != null && calculatedImageSettings != null) {
      if (calculatedImageSettings.excavation != null) {
        setCells(excavateModules(cells, calculatedImageSettings.excavation));
      }
      if (!available) {
        setAvailable(true);
      }
    }
  }, [props.imageSettings, calculatedImageSettings]);
  useEffect(() => {
    const newMargin = getMarginSize(props.includeMargin, props.marginSize);
    setMargin(newMargin);
    setCalculatedImageSettings(
      getImageSettings(cells, props.size, newMargin, props.imageSettings)
    );

    // Drawing strategy: instead of a rect per module, we're going to create a // single path for the dark modules and layer that on top of a light rect, // for a total of 2 DOM nodes. We pay a bit more in string concat but that's
    // way faster than DOM ops.
    // For level 1, 441 nodes -> 2
    // For level 40, 31329 -> 2
    setFgPath(generatePath(cells ?? [], newMargin));
  }, [
    props.size,
    props.imageSettings,
    props.includeMargin,
    props.marginSize,
    cells,
  ]);

  return (
    <svg
      height={props.size}
      width={props.size}
      viewBox={`0 0 ${numCells()} ${numCells()}`}
      className={props.className}
    >
      {!!props.title ? <title>{props.title}</title> : null}
      <path
        shapeRendering="crispEdges"
        fill={props.bgColor}
        d={`M0,0 h${numCells()}v${numCells()}H0z`}
      />
      <path shapeRendering="crispEdges" fill={props.fgColor} d={fgPath} />
      {available && !!props.imageSettings?.src ? (
        <image
          preserveAspectRatio="none"
          href={props.imageSettings.src}
          height={calculatedImageSettings.h}
          width={calculatedImageSettings.w}
          x={calculatedImageSettings.x + margin}
          y={calculatedImageSettings.y + margin}
        />
      ) : null}
    </svg>
  );
}

QRCode.defaultProps = {
  size: DEFAULT_SIZE,
  level: DEFAULT_LEVEL,
  bgColor: DEFAULT_BGCOLOR,
  fgColor: DEFAULT_FGCOLOR,
  includeMargin: DEFAULT_INCLUDEMARGIN,
};

export default QRCode;
